import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CustomFormFieldLookupResponse,
  CustomFormFieldValidationResponse,
} from '../models/CustomFormField';
import { FeatureFlag } from '../models/FeatureFlag';
import { OrderNumberLookupResult } from '../models/OrderNumberLookup';
import { Product } from '../models/Product';
import { ProductHistoryItem } from '../models/ProductHistory';
import { ProductUsageCount } from '../models/ProductUsageCount';
import { ProjectLink } from '../models/ProjectLink';
import { ProvisioningOrder } from '../models/ProvisioningOrder';
import { SupervisorProductCost } from '../models/SupervisorProductCost';
import { UserLicenseStatus } from '../models/UserLicenseStatus';
import { UserSearchItem } from '../models/UserSearchItem';
import {
  BillingValidationOptions,
  BillingValidationResult,
  CancelToolOptions,
  CancelUserLicenseOptions,
  CatalogSnapshot,
  CatalogType,
  ConvertShoppingCartOptions,
  CostCenterLookupOptions,
  CustomFormFieldLookupOptions,
  DeveloperCompassApi,
  GetProductsOptions,
  OrderNumberLookupOptions,
  OrderUserLicenseOptions,
  ProjectPayload,
  ResolveAssetUrlOptions,
  SaveProjectOptions,
  SaveShoppingCartOptions,
  SearchUsersOptions,
  UpdateCustomFormDataOptions,
  UpdateProjectOwnershipOptions,
  UpdateProjectUserRolesOptions,
  UpdateServicePartnerCustomFormDataOptions,
  UpdateServicePartnerDataOptions,
  UpdateToolOptions,
  UpdateUserLicenseOptions,
  UpsertUserOptions,
  UserAuthorizationPayload,
  UserProjectRefPayload,
  UserRefPayload,
  ValidateCustomFormFieldOptions,
  ValidateShoppingCartOptions,
} from './api';
import { AssetService } from './services/AssetService';
import { BillingService } from './services/BillingService';
import { CatalogService } from './services/CatalogService';
import { LicenseService } from './services/LicenseService';
import { ProjectService } from './services/ProjectService';
import { ShoppingCartService } from './services/ShoppingCartService';
import { SupervisorService } from './services/SupervisorService';
import { UserService } from './services/UserService';

export interface DeveloperCompassClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

/**
 * Facade over the Developer Compass backend. Implements
 * `DeveloperCompassApi` by delegating to a set of focused services, each
 * responsible for one area of the backend (catalog, assets, shopping cart,
 * projects, users, licenses, supervisor views and billing). See the
 * `./services` directory for the individual implementations.
 */
export class DeveloperCompassClient implements DeveloperCompassApi {
  private readonly catalog: CatalogService;
  private readonly asset: AssetService;
  private readonly shoppingCart: ShoppingCartService;
  private readonly project: ProjectService;
  private readonly user: UserService;
  private readonly license: LicenseService;
  private readonly supervisor: SupervisorService;
  private readonly billing: BillingService;

  constructor(options: DeveloperCompassClientOptions) {
    this.catalog = new CatalogService(options);
    this.asset = new AssetService(options);
    this.shoppingCart = new ShoppingCartService(options);
    this.project = new ProjectService(options);
    this.user = new UserService(options);
    this.license = new LicenseService(options);
    this.supervisor = new SupervisorService(options);
    this.billing = new BillingService(options);
  }

  // Catalog
  getFeatureFlags(withTarget?: boolean): Promise<FeatureFlag[]> {
    return this.catalog.getFeatureFlags(withTarget);
  }

  getProducts(options: GetProductsOptions): Promise<Product[]> {
    return this.catalog.getProducts(options);
  }

  getCatalog(options: GetProductsOptions): Promise<CatalogSnapshot> {
    return this.catalog.getCatalog(options);
  }

  getAverageProvisioningTimes(
    catalogType: CatalogType,
  ): Promise<Record<string, number>> {
    return this.catalog.getAverageProvisioningTimes(catalogType);
  }

  getServicePartners(): Promise<string[]> {
    return this.catalog.getServicePartners();
  }

  getServicePartnerDetails(
    servicePartnerName: string,
  ): Promise<Record<string, unknown> | undefined> {
    return this.catalog.getServicePartnerDetails(servicePartnerName);
  }

  getCustomFormFieldLookup(
    options: CustomFormFieldLookupOptions,
  ): Promise<CustomFormFieldLookupResponse | undefined> {
    return this.catalog.getCustomFormFieldLookup(options);
  }

  validateCustomFormField(
    options: ValidateCustomFormFieldOptions,
  ): Promise<CustomFormFieldValidationResponse | undefined> {
    return this.catalog.validateCustomFormField(options);
  }

  // Assets
  resolveAssetUrl(
    path: string,
    options?: ResolveAssetUrlOptions,
  ): Promise<string | undefined> {
    return this.asset.resolveAssetUrl(path, options);
  }

  // Shopping cart
  getShoppingCart(key: string): Promise<string | undefined> {
    return this.shoppingCart.getShoppingCart(key);
  }

  saveShoppingCart(options: SaveShoppingCartOptions): Promise<void> {
    return this.shoppingCart.saveShoppingCart(options);
  }

  deleteShoppingCart(key: string): Promise<void> {
    return this.shoppingCart.deleteShoppingCart(key);
  }

  validateShoppingCart(options: ValidateShoppingCartOptions): Promise<void> {
    return this.shoppingCart.validateShoppingCart(options);
  }

  convertShoppingCart(options: ConvertShoppingCartOptions): Promise<void> {
    return this.shoppingCart.convertShoppingCart(options);
  }

  // Projects
  getProject(projectKey: string): Promise<ProjectPayload | undefined> {
    return this.project.getProject(projectKey);
  }

  saveProject(
    options: SaveProjectOptions,
  ): Promise<ProjectPayload | undefined> {
    return this.project.saveProject(options);
  }

  getProjectProvisioningOrders(
    projectKey: string,
  ): Promise<ProvisioningOrder[]> {
    return this.project.getProjectProvisioningOrders(projectKey);
  }

  getToolProvisioningOrders(
    projectKey: string,
    toolId: string,
  ): Promise<ProvisioningOrder[]> {
    return this.project.getToolProvisioningOrders(projectKey, toolId);
  }

  updateProjectOwnership(
    options: UpdateProjectOwnershipOptions,
  ): Promise<void> {
    return this.project.updateProjectOwnership(options);
  }

  updateProjectUserRoles(
    options: UpdateProjectUserRolesOptions,
  ): Promise<void> {
    return this.project.updateProjectUserRoles(options);
  }

  updateServicePartnerData(
    options: UpdateServicePartnerDataOptions,
  ): Promise<boolean> {
    return this.project.updateServicePartnerData(options);
  }

  updateProjectDataSelfAssessment(
    projectKey: string,
    data: unknown,
  ): Promise<void> {
    return this.project.updateProjectDataSelfAssessment(projectKey, data);
  }

  getProjectKeySuggestion(projectName: string): Promise<string | undefined> {
    return this.project.getProjectKeySuggestion(projectName);
  }

  updateToolCustomFormData(
    options: UpdateCustomFormDataOptions,
  ): Promise<void> {
    return this.project.updateToolCustomFormData(options);
  }

  updateServicePartnerCustomFormData(
    options: UpdateServicePartnerCustomFormDataOptions,
  ): Promise<void> {
    return this.project.updateServicePartnerCustomFormData(options);
  }

  cancelTool(options: CancelToolOptions): Promise<void> {
    return this.project.cancelTool(options);
  }

  undoCancelTool(projectKey: string, toolId: string): Promise<void> {
    return this.project.undoCancelTool(projectKey, toolId);
  }

  getProjectToolLinks(projectKey: string): Promise<ProjectLink[]> {
    return this.project.getProjectToolLinks(projectKey);
  }

  updateTool(options: UpdateToolOptions): Promise<void> {
    return this.project.updateTool(options);
  }

  getToolUsageCounts(): Promise<ProductUsageCount[]> {
    return this.project.getToolUsageCounts();
  }

  getOrderNumberLookup(
    options: OrderNumberLookupOptions,
  ): Promise<OrderNumberLookupResult | undefined> {
    return this.project.getOrderNumberLookup(options);
  }

  getOrderNumberCount(ou: string, signal?: AbortSignal): Promise<number> {
    return this.project.getOrderNumberCount(ou, signal);
  }

  getCostCenterLookup(options: CostCenterLookupOptions): Promise<string[]> {
    return this.project.getCostCenterLookup(options);
  }

  getCompleteCostCenterLookup(
    options: CostCenterLookupOptions,
  ): Promise<string[]> {
    return this.project.getCompleteCostCenterLookup(options);
  }

  // Users
  getUserRef(searchTerm: string): Promise<UserRefPayload | undefined> {
    return this.user.getUserRef(searchTerm);
  }

  getUser(userId: string): Promise<UserAuthorizationPayload | undefined> {
    return this.user.getUser(userId);
  }

  upsertUser(
    options: UpsertUserOptions,
  ): Promise<UserAuthorizationPayload | undefined> {
    return this.user.upsertUser(options);
  }

  getUserProjectRef(
    projectKey: string,
  ): Promise<UserProjectRefPayload | undefined> {
    return this.user.getUserProjectRef(projectKey);
  }

  searchUsers(options: SearchUsersOptions): Promise<UserSearchItem[]> {
    return this.user.searchUsers(options);
  }

  confirmProvisioning(path: string, requestId: string): Promise<void> {
    return this.user.confirmProvisioning(path, requestId);
  }

  verifyEmail(verificationCode: string): Promise<void> {
    return this.user.verifyEmail(verificationCode);
  }

  // Licenses
  getUserLicenseUsageCounts(): Promise<ProductUsageCount[]> {
    return this.license.getUserLicenseUsageCounts();
  }

  getUserLicenseProvisioningOrders(): Promise<ProvisioningOrder[]> {
    return this.license.getUserLicenseProvisioningOrders();
  }

  getUserLicenseLinks(): Promise<ProjectLink[]> {
    return this.license.getUserLicenseLinks();
  }

  getUserLicenseHistory(licenseId: string): Promise<ProductHistoryItem[]> {
    return this.license.getUserLicenseHistory(licenseId);
  }

  getUserLicenseStatus(): Promise<UserLicenseStatus[]> {
    return this.license.getUserLicenseStatus();
  }

  orderUserLicense(options: OrderUserLicenseOptions): Promise<void> {
    return this.license.orderUserLicense(options);
  }

  cancelUserLicense(options: CancelUserLicenseOptions): Promise<void> {
    return this.license.cancelUserLicense(options);
  }

  undoCancelUserLicense(licenseId: string): Promise<void> {
    return this.license.undoCancelUserLicense(licenseId);
  }

  updateUserLicense(options: UpdateUserLicenseOptions): Promise<void> {
    return this.license.updateUserLicense(options);
  }

  cancelUserLicenseReview(userId: string, licenseId: string): Promise<void> {
    return this.license.cancelUserLicenseReview(userId, licenseId);
  }

  keepUserLicenseReview(userId: string, licenseId: string): Promise<void> {
    return this.license.keepUserLicenseReview(userId, licenseId);
  }

  updateLicenseCustomFormData(
    options: UpdateCustomFormDataOptions,
  ): Promise<void> {
    return this.license.updateLicenseCustomFormData(options);
  }

  resetTeamMemberBudget(
    userId: string,
    licenseInstanceId: string,
  ): Promise<void> {
    return this.license.resetTeamMemberBudget(userId, licenseInstanceId);
  }

  // Supervisor
  getLicensesUnderSupervisor(
    catalogRefId?: string,
  ): Promise<SupervisorProductCost[]> {
    return this.supervisor.getLicensesUnderSupervisor(catalogRefId);
  }

  getToolsUnderSupervisor(): Promise<SupervisorProductCost[]> {
    return this.supervisor.getToolsUnderSupervisor();
  }

  getToolCostUnderSupervisor(
    catalogRefId: string,
  ): Promise<SupervisorProductCost | undefined> {
    return this.supervisor.getToolCostUnderSupervisor(catalogRefId);
  }

  // Billing
  validateBillingInformation(
    options: BillingValidationOptions,
  ): Promise<BillingValidationResult> {
    return this.billing.validateBillingInformation(options);
  }
}
